/**
 * Generate a small WebP thumbnail Blob from an image File.
 * - Aspect-ratio preserved; longest side capped at `maxSize`.
 * - Output is image/webp at the given quality (0–1). Browsers that support
 *   OffscreenCanvas are used; fall back to a hidden <canvas> for older Safari.
 * - Returns null on unsupported file types or decode failures (caller should
 *   skip the thumb upload in that case).
 */
// Blob → data-URI string, for storing a thumbnail INLINE on a record. Resolves
// '' on failure so a thumbnail is never the thing that fails an upload.
export function blobToDataURL(blob) {
  return new Promise((resolve) => {
    if (!blob) return resolve('')
    const r = new FileReader()
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : '')
    r.onerror = () => resolve('')
    r.readAsDataURL(blob)
  })
}

export async function generateThumbnail(file, { maxSize = 240, quality = 0.8 } = {}) {
  if (!file || !/^image\//.test(file.type || '')) return null
  let bitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return null
  }
  const ratio = bitmap.width / bitmap.height
  let w, h
  if (ratio >= 1) {
    w = Math.min(maxSize, bitmap.width)
    h = Math.round(w / ratio)
  } else {
    h = Math.min(maxSize, bitmap.height)
    w = Math.round(h * ratio)
  }
  try {
    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(w, h)
      const ctx = canvas.getContext('2d')
      ctx.drawImage(bitmap, 0, 0, w, h)
      return await canvas.convertToBlob({ type: 'image/webp', quality })
    }
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h)
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
  } catch {
    return null
  } finally {
    bitmap.close?.()
  }
}
